import type {
  FactRaw,
  ConditionRaw,
  EventRaw,
  ActionRaw,
  RuleRaw,
  EventRuleRaw,
  StateRuleRaw,
  SaveResponseActionRaw,
  UpdateFieldActionRaw,
  ChangeAccessActionRaw,
  SendNotificationActionRaw,
  // UpdateWorkflowActionRaw,
  CrossProcessSaveResponseActionRaw,
  CreateChildWorkflowActionRaw,
  ScheduleIntegrationActionRaw,
  CreateChildWorkflowFromIntegrationResultActionRaw,
  StepActionRaw,
  AssignStepActionRaw,
  StartWorkflowActionRaw,
  NameStepVersionActionRaw,
  SyncDataObjectActionRaw,
  UpdateStepLockActionRaw,
  UpdateWorkflowV2ActionRaw,
  ScreenEntityActionRaw,
  InviteThirdPartyUsersActionRaw,
  TriggerHermesIntegrationActionRaw,
  DeregisterScreeningRequestsActionRaw,
  LinkDataObjectActionRaw,
  BulkStatusUpdateDataObjectActionRaw,
  GeneratePdfFromDocActionRaw,
  ScreenRelatedWorkflowsActionRaw,
  CreateChildWorkflowV2ActionRaw,
  SetObjectRelationshipActionRaw,
  BulkLinkChildWorkflowsActionRaw,
  UnsetObjectRelationshipActionRaw,
  CreateSurveyActionRaw,
  DelinkWorkflowActionRaw,
  EventRuleRawAppJSON,
  RuleRawAppJSON,
  ActionRawAppJSON,
  EventRawAppJSON,
  StateRuleRawAppJSON,
  SetScreeningUpdateEventStatusActionRaw,
  SendTestSurveyEmailActionRaw
} from "@certa/queries/schemas/studio/eca.schema";
import type {
  Fact,
  Condition,
  Event,
  Rule,
  EventRule,
  StateRule
} from "@certa/studio-core/src/types/eca.types";

import type { Process } from "@certa/studio-core/src/types/process.types";
import { isEmpty } from "lodash-es";

// Utility type to add __studio_id property to action types
type ActionCommonRaw<T> = T & { __studio_id?: string };

export const createFactRaw = (fact: Fact): FactRaw => ({
  type: fact.type,
  value: fact.value,
  object_type: fact.objectType,
  object_tag: fact.objectTag,
  object_attribute: fact.objectAttribute,
  reverse: fact.reverse,
  operation: fact.operation,
  variables: fact.variables?.map(createFactRaw),
  options: fact.options
    ? {
        weights: fact.options.weights,
        delimiter: fact.options.delimiter,
        start: fact.options.start,
        replace_string: fact.options.replaceString,
        replace_with: fact.options.replaceWith,
        index: fact.options.index,
        diff_param: fact.options.diffParam,
        default_value: fact.options.defaultValue,
        from_format: fact.options.fromFormat,
        values_to_replace: fact.options.valuesToReplace,
        units: fact.options.units,
        subtract: fact.options.subtract,
        precision: fact.options.precision,
        end: fact.options.end
      }
    : undefined,
  source: fact.source ? createFactRaw(fact.source) : undefined,
  query: fact.query,
  data_attribute: fact?.dataAttribute || undefined,
  relation_path: fact?.relationPath || undefined,
  settings_type: fact.settingsType,
  settings_name: fact.settingsName,
  clauses: fact.clauses?.map(clause => ({
    condition: createConditionRaw(clause.condition),
    then: createFactRaw(clause.then)
  })),
  default: fact.default ? createFactRaw(fact.default) : undefined,
  user_identifier: fact.userIdentifier
    ? createFactRaw(fact.userIdentifier)
    : undefined,
  user_email: fact.userEmail ? createFactRaw(fact.userEmail) : undefined,
  attribute: fact.attribute,
  extra_json_tags: fact.extraJsonTags,
  fetch_integration_json_for_fields: fact.fetchIntegrationJsonForFields,
  tag: fact.tag,
  data_path: fact.dataPath
});

const createConditionRaw = (condition: Condition): ConditionRaw => ({
  operator: condition.operator,
  left: condition.left ? createFactRaw(condition.left) : undefined,
  right: condition.right ? createFactRaw(condition.right) : undefined,
  any: condition.any?.map(createConditionRaw),
  all: condition.all?.map(createConditionRaw)
});

const createEventRaw = (
  event: Event,
  shouldIgnoreId: boolean = true
): EventRaw | EventRawAppJSON => {
  const __studioId = shouldIgnoreId ? undefined : event.__studioId;
  switch (event.name) {
    case "create_workflow":
    case "adjudicate_workflow":
    case "change_third_party_user_access": {
      return {
        __studio_id: __studioId,
        name: event.name
      };
    }

    case "archive_workflow": {
      return {
        __studio_id: __studioId,
        name: event.name,
        extra: {
          source_kind: event.extra.sourceKind
        }
      };
    }

    case "submit_step":
    case "undo_step":
    case "initiate_step":
    case "save_response":
    case "receive_integration":
    case "select_integration_result":
    case "adjudicate_integration_result":
    case "manual_integration_trigger":
    case "screening_initiated": {
      return {
        __studio_id: __studioId,
        name: event.name,
        on_tag: event.onTag
      };
    }

    case "create_object":
    case "update_object": {
      return {
        __studio_id: __studioId,
        name: event.name,
        extra: {
          source_kind: event.extra.sourceKind,
          object_type: event.extra.objectType,
          object_reference: event.extra.objectReference
        }
      };
    }

    default: {
      // @ts-expect-error - This will not have error if you missed adding a case
      // having error = all known cases are handled.
      throw new Error(`Unknown event name: ${event.name}`);
    }
  }
};

const createActionRaw = (
  action: EventRule["rules"][0]["actions"][0],
  shouldIgnoreId: boolean = true
): ActionRaw | ActionRawAppJSON => {
  const __studioId = shouldIgnoreId ? undefined : action.__studioId;
  switch (action.name) {
    case "save_response": {
      const saveResponseActionRaw: ActionCommonRaw<SaveResponseActionRaw> = {
        __studio_id: __studioId,
        name: "save_response",
        params: {
          tag: action.params.tag,
          attr: action.params.attr,
          value: createFactRaw(action.params.value),
          aggregate_by: action.params.aggregateBy,
          length: action.params.length
        },
        order: action.order
      };
      return saveResponseActionRaw;
    }

    case "update_field": {
      const updateFieldActionRaw: ActionCommonRaw<UpdateFieldActionRaw> = {
        __studio_id: __studioId,
        name: "update_field",
        params: {
          tag: action.params.tag,
          is_enabled: action.params.isEnabled
            ? createFactRaw(action.params.isEnabled)
            : undefined,
          is_required: action.params.isRequired
            ? createFactRaw(action.params.isRequired)
            : undefined,
          is_read_only: action.params.isReadOnly
            ? createFactRaw(action.params.isReadOnly)
            : undefined,
          regex_value: action.params.regexValue,
          regex_error: action.params.regexError,
          status_indicator: action.params.statusIndicator
        },
        order: action.order
      };
      return updateFieldActionRaw;
    }

    case "submit_step":
    case "undo_step":
    case "enable_step":
    case "disable_step": {
      const stepActionRaw: ActionCommonRaw<StepActionRaw> = {
        __studio_id: __studioId,
        name: action.name,
        params: {
          tag: action.params.tag
        },
        order: action.order
      };
      return stepActionRaw;
    }

    case "assign_step": {
      const assignStepActionRaw: ActionCommonRaw<AssignStepActionRaw> = {
        __studio_id: __studioId,
        name: "assign_step",
        params: {
          tag: action.params.tag,
          users: action.params.users.map(createFactRaw),
          user_tag: action.params.userTag
        },
        order: action.order
      };
      return assignStepActionRaw;
    }

    case "change_access": {
      const common = {
        static_groups_to_add: action.params.staticGroupsToAdd,
        static_groups_to_remove: action.params.staticGroupsToRemove,
        dynamic_groups_to_add: action.params.dynamicGroupsToAdd,
        dynamic_groups_to_remove: action.params.dynamicGroupsToRemove
      };
      const changeAccessActionRaw: ActionCommonRaw<ChangeAccessActionRaw> = {
        __studio_id: __studioId,
        name: "change_access",
        params:
          action.params.objectType === "workflow"
            ? {
                object_type: action.params.objectType,
                ...common
              }
            : {
                object_type: action.params.objectType,
                object_tag: action.params.objectTag,
                ...common
              },
        order: action.order
      };
      return changeAccessActionRaw;
    }

    case "name_step_version": {
      const nameStepVersionActionRaw: ActionCommonRaw<NameStepVersionActionRaw> =
        {
          __studio_id: __studioId,
          name: "name_step_version",
          params: {
            tag: action.params.tag,
            label: action.params.label,
            color: action.params.color
          },
          order: action.order
        };
      return nameStepVersionActionRaw;
    }

    case "update_workflow_v2": {
      const updateWorkflowActionRaw: ActionCommonRaw<UpdateWorkflowV2ActionRaw> =
        {
          __studio_id: __studioId,
          name: "update_workflow_v2",
          params: {
            name: action.params.name
              ? createFactRaw(action.params.name)
              : undefined,
            lc_id: action.params.lcId
              ? createFactRaw(action.params.lcId)
              : undefined,
            lc_message: action.params.lcMessage
              ? createFactRaw(action.params.lcMessage)
              : undefined,
            lc_due_date: action.params.lcDueDate
              ? createFactRaw(action.params.lcDueDate)
              : undefined,
            external_id: action.params.externalId
              ? createFactRaw(action.params.externalId)
              : undefined,
            lc_data: action.params.lcData
              ? Object.fromEntries(
                  Object.entries(action.params.lcData).map(([k, v]) => [
                    k,
                    createFactRaw(v)
                  ])
                )
              : undefined,
            logo: action.params.logo
              ? createFactRaw(action.params.logo)
              : undefined,
            status: action.params.status
              ? createFactRaw(action.params.status)
              : undefined,
            business_unit: action.params.businessUnit
              ? createFactRaw(action.params.businessUnit)
              : undefined,
            region: action.params.region
          },
          order: action.order
        };
      return updateWorkflowActionRaw;
    }

    case "send_notification": {
      const sendNotificationActionRaw: ActionCommonRaw<SendNotificationActionRaw> =
        {
          __studio_id: __studioId,
          name: "send_notification",
          params: {
            template: action.params.template,
            to_emails: createFactRaw(action.params.toEmails),
            cc_emails: action.params.ccEmails
              ? createFactRaw(action.params.ccEmails)
              : undefined,
            bcc_emails: action.params.bccEmails
              ? createFactRaw(action.params.bccEmails)
              : undefined,
            reply_to_email: action.params.replyToEmail
              ? createFactRaw(action.params.replyToEmail)
              : undefined,
            reply_to_field: action.params.replyToField,
            steps_to_include: action.params.stepsToInclude,
            data_object_reference: action.params.dataObjectReference,
            language: action.params.language
              ? createFactRaw(action.params.language)
              : undefined
          },
          order: action.order
        };
      return sendNotificationActionRaw;
    }

    case "cross_process_save_response": {
      const crossProcessSaveResponseActionRaw: ActionCommonRaw<CrossProcessSaveResponseActionRaw> =
        {
          __studio_id: __studioId,
          name: "cross_process_save_response",
          params: {
            related_processes: action.params.relatedProcesses,
            source: createFactRaw(action.params.source),
            target: action.params.target,
            aggregation_policy: action.params.aggregationPolicy,
            length: action.params.length
          },
          order: action.order
        };
      return crossProcessSaveResponseActionRaw;
    }

    case "create_child_workflow": {
      const createChildWorkflowActionRaw: ActionCommonRaw<CreateChildWorkflowActionRaw> =
        {
          __studio_id: __studioId,
          name: "create_child_workflow",
          params: {
            name: createFactRaw(action.params.name),
            kind: action.params.kind,
            single_field_values: action.params.singleFieldValues
              ? Object.fromEntries(
                  Object.entries(action.params.singleFieldValues).map(
                    ([k, v]) => [k, createFactRaw(v)]
                  )
                )
              : undefined,
            indexed_field_values: action.params.indexedFieldValues
              ? Object.fromEntries(
                  Object.entries(action.params.indexedFieldValues).map(
                    ([k, v]) => [k, createFactRaw(v)]
                  )
                )
              : undefined
          },
          order: action.order
        };
      return createChildWorkflowActionRaw;
    }

    case "start_workflow": {
      const startWorkflowActionRaw: ActionCommonRaw<StartWorkflowActionRaw> = {
        __studio_id: __studioId,
        name: "start_workflow",
        params: {
          kind: action.params.kind,
          name: createFactRaw(action.params.name),
          field_values: Object.fromEntries(
            Object.entries(action.params.fieldValues).map(([k, v]) => [
              k,
              createFactRaw(v)
            ])
          ),
          data_object_reference_to_objects: action.params
            .dataObjectReferenceToObjects
            ? Object.fromEntries(
                Object.entries(action.params.dataObjectReferenceToObjects).map(
                  ([k, v]) => [k, createFactRaw(v)]
                )
              )
            : undefined
        },
        order: action.order
      };
      return startWorkflowActionRaw;
    }

    case "schedule_integration": {
      const scheduleIntegrationActionRaw: ActionCommonRaw<ScheduleIntegrationActionRaw> =
        {
          __studio_id: __studioId,
          name: "schedule_integration",
          params: {
            tag: action.params.tag,
            api_key_map: action.params.apiKeyMap
              ? Object.fromEntries(
                  Object.entries(action.params.apiKeyMap).map(([k, v]) => [
                    k,
                    createFactRaw(v)
                  ])
                )
              : undefined,
            poll: action.params.poll,
            polling_interval: action.params.pollingInterval
          },
          order: action.order
        };
      return scheduleIntegrationActionRaw;
    }

    case "create_child_workflow_from_integration_result": {
      const createChildWorkflowFromIntegrationResultActionRaw: ActionCommonRaw<CreateChildWorkflowFromIntegrationResultActionRaw> =
        {
          __studio_id: __studioId,
          name: "create_child_workflow_from_integration_result",
          params: {
            tag: action.params.tag
          },
          order: action.order
        };
      return createChildWorkflowFromIntegrationResultActionRaw;
    }

    case "sync_data_object": {
      const syncDataObjectActionRaw: ActionCommonRaw<SyncDataObjectActionRaw> =
        {
          __studio_id: __studioId,
          name: "sync_data_object",
          params: {
            data_object_type: action.params.dataObjectType,
            data_object_name: createFactRaw(action.params.dataObjectName),
            data_mapping: action.params.dataMapping?.map(mapping => ({
              data_object_attribute: mapping.dataObjectAttribute,
              workflow_attribute: createFactRaw(mapping.workflowAttribute),
              operation: mapping.operation
            })),
            data_object_reference: action.params.dataObjectReference,
            set_logo: action.params.setLogo,
            dynamic_group_role_mapping: action.params.dynamicGroupRoleMapping,
            data_object_status: action.params.dataObjectStatus,
            sharable_groups_in_hierarchy:
              action.params.sharableGroupsInHierarchy,
            duplicate_check_context: action.params.duplicateCheckContext
              ? {
                  parent_object_reference:
                    action.params.duplicateCheckContext.parentObjectReference,
                  relation_path:
                    action.params.duplicateCheckContext.relationPath,
                  object_status:
                    action.params.duplicateCheckContext.objectStatus
                }
              : undefined,
            save_object_created_flag_to_field:
              action.params.saveObjectCreatedFlagToField
          },
          order: action.order
        };
      return syncDataObjectActionRaw;
    }

    case "update_step_lock": {
      const updateStepLockActionRaw: ActionCommonRaw<UpdateStepLockActionRaw> =
        {
          __studio_id: __studioId,
          name: "update_step_lock",
          params: {
            tag: action.params.tag,
            is_locked: createFactRaw(action.params.isLocked)
          },
          order: action.order
        };
      return updateStepLockActionRaw;
    }

    case "screen_entity": {
      const screenEntityActionRaw: ActionCommonRaw<ScreenEntityActionRaw> = {
        __studio_id: __studioId,
        name: "screen_entity",
        params: {
          tag: action.params.tag,
          provider: action.params.provider,
          name: createFactRaw(action.params.name),
          request_data: action.params.requestData
            ? Object.fromEntries(
                Object.entries(action.params.requestData).map(([k, v]) => [
                  k,
                  createFactRaw(v)
                ])
              )
            : undefined,
          skip_index_distribution_keys: action.params.skipIndexDistributionKeys
            ? action.params.skipIndexDistributionKeys
            : undefined,
          dedupe_config: action.params.dedupeConfig
            ? {
                required: action.params.dedupeConfig.required
              }
            : undefined
        },
        order: action.order
      };
      return screenEntityActionRaw;
    }
    case "invite_third_party_users": {
      const inviteThirdPartyUsersActionRaw: ActionCommonRaw<InviteThirdPartyUsersActionRaw> =
        {
          __studio_id: __studioId,
          name: "invite_third_party_users",
          params: {
            emails: action.params.emails.map(createFactRaw),
            dynamic_group_names: action.params.dynamicGroupNames
          },
          order: action.order
        };
      return inviteThirdPartyUsersActionRaw;
    }

    case "trigger_hermes_integration": {
      const triggerHermesIntegrationActionRaw: ActionCommonRaw<TriggerHermesIntegrationActionRaw> =
        {
          __studio_id: __studioId,
          name: "trigger_hermes_integration",
          params: {
            tag: action.params.tag,
            payload_map: action.params.payloadMap
          },
          order: action.order
        };
      return triggerHermesIntegrationActionRaw;
    }

    case "deregister_screening_requests": {
      const deregisterScreeningRequestsActionRaw: ActionCommonRaw<DeregisterScreeningRequestsActionRaw> =
        {
          __studio_id: __studioId,
          name: "deregister_screening_requests",
          params: {
            tag: action.params.tag
          },
          order: action.order
        };
      return deregisterScreeningRequestsActionRaw;
    }

    case "link_data_object": {
      const linkDataObjectActionRaw: ActionCommonRaw<LinkDataObjectActionRaw> =
        {
          __studio_id: __studioId,
          name: "link_data_object",
          params: {
            data_object_references: action.params.dataObjectReferences
              ? Object.fromEntries(
                  Object.entries(action.params.dataObjectReferences).map(
                    ([key, value]) => [
                      key,
                      {
                        type: "data_object_reference",
                        value: value.value,
                        relation_path: value.relationPath
                      }
                    ]
                  )
                )
              : {}
          },
          order: action.order
        };
      return linkDataObjectActionRaw;
    }

    case "bulk_status_update_data_object": {
      const bulkStatusUpdateDataObjectActionRaw: ActionCommonRaw<BulkStatusUpdateDataObjectActionRaw> =
        {
          __studio_id: __studioId,
          name: "bulk_status_update_data_object",
          params: {
            data_object_reference:
              action.params.dataObjectReference || undefined,
            rel_paths_status_map: action.params.relPathsStatusMap || undefined,
            object_ids: action.params.objectIds
              ? createFactRaw(action.params.objectIds)
              : undefined,
            status: action.params.status
          },
          order: action.order
        };
      return bulkStatusUpdateDataObjectActionRaw;
    }

    case "generate_pdf_from_doc": {
      const generatePdfFromDocActionRaw: ActionCommonRaw<GeneratePdfFromDocActionRaw> =
        {
          __studio_id: __studioId,
          name: "generate_pdf_from_doc",
          params: {
            name_format: action.params.nameFormat,
            target_field_tag: action.params.targetFieldTag,
            version_field_tag: action.params.versionFieldTag,
            render_document_url: action.params.renderDocumentUrl,
            context_template_slug: action.params.contextTemplateSlug
          },
          order: action.order
        };
      return generatePdfFromDocActionRaw;
    }

    case "screen_related_workflows": {
      const screenRelatedWorkflowsActionRaw: ActionCommonRaw<ScreenRelatedWorkflowsActionRaw> =
        {
          __studio_id: __studioId,
          name: "screen_related_workflows",
          params: {
            screening_field_tag: action.params.screeningFieldTag,
            provider: action.params.provider,
            related_workflow_mappings:
              action.params.relatedWorkflowMappings.map(mapping => ({
                child_kind: mapping.childKind,
                should_screen_field: mapping.shouldScreenField,
                search_request_id_field: mapping.searchRequestIdField,
                search_request_name_field: mapping.searchRequestNameField,
                request_data_mapping: mapping.requestDataMapping
              })),
            dedupe_config: action.params?.dedupeConfig
              ? {
                  required: action.params.dedupeConfig?.required,
                  dedupe_against_parent_object:
                    action.params.dedupeConfig?.dedupeAgainstParentObject,
                  parent_object_reference:
                    action.params.dedupeConfig?.parentObjectReference
                }
              : undefined
          },
          order: action.order
        };
      return screenRelatedWorkflowsActionRaw;
    }

    case "create_child_workflow_v2": {
      const createChildWorkflowV2ActionRaw: ActionCommonRaw<CreateChildWorkflowV2ActionRaw> =
        {
          __studio_id: __studioId,
          name: "create_child_workflow_v2",
          params: {
            kind: action.params.kind,
            name: {
              type: action.params.name.type,
              fact: createFactRaw(action.params.name.fact)
            },
            field_values: Object.fromEntries(
              Object.entries(action.params.fieldValues).map(([k, v]) => [
                k,
                {
                  type: v.type,
                  fact: createFactRaw(v.fact)
                }
              ])
            ),
            duplicate_check: action.params.duplicateCheck
              ? {
                  check_across: action.params.duplicateCheck.checkAcross,
                  fuzzy_threshold: action.params.duplicateCheck.fuzzyThreshold,
                  update_duplicate_workflow:
                    action.params.duplicateCheck.updateDuplicateWorkflow,
                  update_duplicate_workflow_answers:
                    action.params.duplicateCheck.updateDuplicateWorkflowAnswers,
                  filter_logic: action.params.duplicateCheck.filterLogic
                    ? {
                        operator:
                          action.params.duplicateCheck.filterLogic.operator,
                        conditions: action.params.duplicateCheck.filterLogic
                          .conditions
                          ? action.params.duplicateCheck.filterLogic.conditions.map(
                              c => ({
                                field: c.field,
                                match_type: c.matchType
                              })
                            )
                          : undefined
                      }
                    : undefined
                }
              : undefined
          },
          order: action.order
        };
      return createChildWorkflowV2ActionRaw;
    }

    case "set_object_relationship": {
      const setObjectRelationshipActionRaw: ActionCommonRaw<SetObjectRelationshipActionRaw> =
        {
          __studio_id: __studioId,
          name: "set_object_relationship",
          params: {
            object: {
              type: action.params.object.type,
              value: action.params.object.value,
              relation_path: action.params.object.relationPath
            },
            to_link_objects: {
              workflows: {
                kind: action.params.toLinkObjects.workflows.kind,
                level: action.params.toLinkObjects.workflows.level
              },
              object_reference: action.params.toLinkObjects.objectReference
            },
            relation_name: action.params.relationName,
            reverse_relationship: action.params.reverseRelationship
          },
          order: action.order
        };
      return setObjectRelationshipActionRaw;
    }
    case "bulk_link_child_workflows": {
      const bulkLinkChildWorkflowsActionRaw: ActionCommonRaw<BulkLinkChildWorkflowsActionRaw> =
        {
          __studio_id: __studioId,
          name: "bulk_link_child_workflows",
          params: {
            common_parent: {
              kind: action.params.commonParent.kind,
              level: action.params.commonParent.level
            },
            kind_tag: action.params.kindTag,
            relation_type: action.params.relationType
          },
          order: action.order
        };
      return bulkLinkChildWorkflowsActionRaw;
    }
    case "unset_object_relationship": {
      const unsetObjectRelationshipActionRaw: ActionCommonRaw<UnsetObjectRelationshipActionRaw> =
        {
          __studio_id: __studioId,
          name: "unset_object_relationship",
          params: {
            data_object: {
              type: action.params.dataObject.type,
              value: action.params.dataObject.value,
              relation_path: action.params.dataObject.relationPath
            },
            relation_name: action.params.relationName,
            object_to_unset: createFactRaw(action.params.objectToUnset)
          },
          order: action.order
        };
      return unsetObjectRelationshipActionRaw;
    }

    case "create_survey": {
      const createSurveyActionRaw: ActionCommonRaw<CreateSurveyActionRaw> = {
        __studio_id: __studioId,
        name: "create_survey",
        params: {
          tag: action.params.tag
        },
        order: action.order
      };
      return createSurveyActionRaw;
    }

    case "delink_workflow": {
      const delinkWorkflowActionRaw: ActionCommonRaw<DelinkWorkflowActionRaw> =
        {
          __studio_id: __studioId,
          name: "delink_workflow",
          params: {
            relation_type: action.params.relationType,
            workflow: createFactRaw(action.params.workflow)
          },
          order: action.order
        };
      return delinkWorkflowActionRaw;
    }

    case "set_screening_update_event_status": {
      const setScreeningUpdateEventStatusActionRaw: ActionCommonRaw<SetScreeningUpdateEventStatusActionRaw> =
        {
          __studio_id: __studioId,
          name: "set_screening_update_event_status",
          params: {
            tag: action.params.tag,
            status: action.params.status
          },
          order: action.order
        };
      return setScreeningUpdateEventStatusActionRaw;
    }

    case "send_test_survey_email": {
      const sendTestSurveyEmailActionRaw: ActionCommonRaw<SendTestSurveyEmailActionRaw> =
        {
          __studio_id: __studioId,
          name: "send_test_survey_email",
          params: {
            kind_tag: createFactRaw(action.params.kindTag),
            recipient_email: createFactRaw(action.params.recipientEmail)
          },
          order: action.order
        };
      return sendTestSurveyEmailActionRaw;
    }

    default:
      // @ts-expect-error - This will not have error if you missed adding a case
      // having error = all known cases are handled.
      throw new Error(`Unknown action name: ${action.name}`);
  }
};

const createRuleRaw = (
  rule: Rule,
  shouldIgnoreId: boolean = true
): RuleRaw | RuleRawAppJSON => ({
  __studio_id: shouldIgnoreId ? undefined : rule.__studioId,
  name: rule.name,
  conditions:
    rule.conditions && !isEmpty(rule.conditions)
      ? createConditionRaw(rule.conditions)
      : undefined,
  actions: rule.actions.map(action => createActionRaw(action, shouldIgnoreId))
});

export const createEventRuleRaw = (
  eventRule: EventRule,
  shouldIgnoreId: boolean = true
): EventRuleRaw | EventRuleRawAppJSON => ({
  __studio_id: shouldIgnoreId ? undefined : eventRule.__studioId,
  rules: eventRule.rules.map(rule => createRuleRaw(rule, shouldIgnoreId)),
  events: eventRule.events.map(event => createEventRaw(event, shouldIgnoreId))
});

export const createStateRuleRaw = (
  stateRule: StateRule,
  shouldIgnoreId: boolean = true
): StateRuleRaw | StateRuleRawAppJSON => {
  const __studioId = shouldIgnoreId ? undefined : stateRule.__studioId;
  switch (stateRule.name) {
    case "save_response": {
      return {
        __studio_id: __studioId,
        name: "save_response",
        params: {
          tag: stateRule.params.tag,
          attr: stateRule.params.attr,
          value: createFactRaw(stateRule.params.value),
          aggregate_by: stateRule.params.aggregateBy,
          length: stateRule.params.length
        }
      };
    }

    case "update_field": {
      return {
        __studio_id: __studioId,
        name: "update_field",
        params: {
          tag: stateRule.params.tag,
          is_enabled: stateRule.params.isEnabled
            ? createFactRaw(stateRule.params.isEnabled)
            : undefined,
          is_required: stateRule.params.isRequired
            ? createFactRaw(stateRule.params.isRequired)
            : undefined,
          is_read_only: stateRule.params.isReadOnly
            ? createFactRaw(stateRule.params.isReadOnly)
            : undefined,
          regex_value: stateRule.params.regexValue,
          regex_error: stateRule.params.regexError,
          status_indicator: stateRule.params.statusIndicator
        }
      };
    }

    case "update_workflow_v2": {
      return {
        __studio_id: __studioId,
        name: "update_workflow_v2",
        params: {
          name: stateRule.params.name
            ? createFactRaw(stateRule.params.name)
            : undefined,
          lc_id: stateRule.params.lcId
            ? createFactRaw(stateRule.params.lcId)
            : undefined,
          lc_message: stateRule.params.lcMessage
            ? createFactRaw(stateRule.params.lcMessage)
            : undefined,
          lc_due_date: stateRule.params.lcDueDate
            ? createFactRaw(stateRule.params.lcDueDate)
            : undefined,
          external_id: stateRule.params.externalId
            ? createFactRaw(stateRule.params.externalId)
            : undefined,
          lc_data: stateRule.params.lcData
            ? Object.fromEntries(
                Object.entries(stateRule.params.lcData).map(([k, v]) => [
                  k,
                  createFactRaw(v)
                ])
              )
            : undefined,
          logo: stateRule.params.logo
            ? createFactRaw(stateRule.params.logo)
            : undefined,
          status: stateRule.params.status
            ? createFactRaw(stateRule.params.status)
            : undefined,
          business_unit: stateRule.params.businessUnit
            ? createFactRaw(stateRule.params.businessUnit)
            : undefined,
          region: stateRule.params.region
            ? createFactRaw(stateRule.params.region)
            : undefined
        }
      };
    }
  }
};

export const forwardParseECA = (
  metaJSON: Process,
  shouldIgnoreId: boolean = true
): {
  event_rules: EventRuleRaw[] | EventRuleRawAppJSON[];
  states: StateRuleRaw[] | StateRuleRawAppJSON[];
} => {
  // Use sequences to maintain order, fallback to Object.values if sequence is not available
  const eventRules = metaJSON.process.eventRuleSequence?.length
    ? metaJSON.process.eventRuleSequence
        .map(__studioId => metaJSON.eventRules?.[__studioId])
        .filter((rule): rule is EventRule => Boolean(rule))
        .map(eventRules => createEventRuleRaw(eventRules, shouldIgnoreId))
    : Object.values(metaJSON.eventRules || {}).map(eventRules =>
        createEventRuleRaw(eventRules, shouldIgnoreId)
      );

  const stateRules = metaJSON.process.stateRuleSequence?.length
    ? metaJSON.process.stateRuleSequence
        .map(__studioId => metaJSON.stateRules?.[__studioId])
        .filter((rule): rule is StateRule => Boolean(rule))
        .map(stateRules => createStateRuleRaw(stateRules, shouldIgnoreId))
    : Object.values(metaJSON.stateRules || {}).map(stateRules =>
        createStateRuleRaw(stateRules, shouldIgnoreId)
      );

  return {
    event_rules: eventRules,
    states: stateRules
  };
};
